<template>
  <el-container class="lf-admin-layout">
    <el-aside width="232px" class="admin-aside">
      <div class="admin-brand">
        <img :src="branding.logo" :alt="branding.logoAlt" class="admin-logo">
        <div>
          <div class="admin-title">LF Admin</div>
          <div class="admin-subtitle">LearningFirst</div>
        </div>
      </div>
      <el-menu
        class="admin-menu"
        aria-label="Admin navigation"
        :default-active="activePath"
        :default-openeds="defaultOpenGroups"
        unique-opened
        router
      >
        <el-submenu v-for="group in navigationGroups" :key="group.id" :index="group.id">
          <template slot="title">
            <i :class="group.icon" aria-hidden="true"></i>
            <span>{{ group.title }}</span>
          </template>
          <el-menu-item v-for="item in group.items" :key="item.path" :index="item.path">
            <span slot="title">{{ item.title }}</span>
          </el-menu-item>
        </el-submenu>
      </el-menu>
    </el-aside>

    <el-container>
      <el-header height="56px" class="admin-header">
        <el-breadcrumb class="current-section" separator="/">
          <el-breadcrumb-item v-if="activeGroup">{{ activeGroup.title }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ currentTitle }}</el-breadcrumb-item>
        </el-breadcrumb>
        <el-button size="small" @click="logout">Log out</el-button>
      </el-header>
      <el-main class="admin-main">
        <router-view />
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import branding from '@/config/branding'
import { learningFirstAdminLogout } from '@/apis/lfAdmin'
import { clearLfAdminSession } from '@/utils/lfAdminSession'

export default {
  name: 'LFAdminLayout',
  data() {
    return {
      branding,
      navigationGroups: [
        {
          id: 'insights',
          title: 'Insights',
          icon: 'el-icon-data-analysis',
          items: [
            { path: '/lf-admin/dashboard', title: 'Dashboard' },
            { path: '/lf-admin/user-behavior', title: 'User Behavior' },
            { path: '/lf-admin/reading-activity', title: 'Reading Activity' },
            { path: '/lf-admin/markdown-stats', title: 'Markdown Stats' }
          ]
        },
        {
          id: 'content',
          title: 'Content',
          icon: 'el-icon-collection',
          items: [
            { path: '/lf-admin/idea-proposals', title: 'Idea Proposals' },
            { path: '/lf-admin/collections', title: 'Collection Pipeline' },
            { path: '/lf-admin/editor-bundles', title: 'Editor Bundles' },
            { path: '/lf-admin/track', title: 'LF Track' },
            { path: '/lf-admin/special-labels', title: 'Special Labels' }
          ]
        },
        {
          id: 'programs',
          title: 'Programs',
          icon: 'el-icon-school',
          items: [
            { path: '/lf-admin/educators', title: 'Educator Programs' },
            { path: '/lf-admin/educator-proposals', title: 'Teacher Proposals' }
          ]
        },
        {
          id: 'people',
          title: 'People & Support',
          icon: 'el-icon-user',
          items: [
            { path: '/lf-admin/curators', title: 'Curator Accounts' },
            { path: '/lf-admin/account-access', title: 'Account Access' },
            { path: '/lf-admin/contact-submissions', title: 'Contact Submissions' }
          ]
        },
        {
          id: 'billing',
          title: 'Billing & Partners',
          icon: 'el-icon-bank-card',
          items: [
            { path: '/lf-admin/platform-billing', title: 'Platform Billing' },
            { path: '/lf-admin/vouchers', title: 'Vouchers' },
            { path: '/lf-admin/affiliate-program', title: 'Affiliate Program' }
          ]
        },
        {
          id: 'ai',
          title: 'AI & Settings',
          icon: 'el-icon-setting',
          items: [
            { path: '/lf-admin/ai-search-review', title: 'AI Search Review' },
            { path: '/lf-admin/model-config', title: 'Model Config' }
          ]
        }
      ]
    }
  },
  computed: {
    activePath() {
      return this.$route.path
    },
    activeGroup() {
      return this.navigationGroups.find(group => group.items.some(item => item.path === this.activePath))
    },
    defaultOpenGroups() {
      return this.activeGroup ? [this.activeGroup.id] : []
    },
    currentTitle() {
      return (this.$route.meta && this.$route.meta.title) || 'LF Admin'
    }
  },
  methods: {
    async logout() {
      try {
        await learningFirstAdminLogout()
      } catch (error) {
        console.error(error)
      }
      clearLfAdminSession()
      this.$router.replace('/admin-login')
    }
  }
}
</script>

<style scoped lang="scss">
.lf-admin-layout {
  min-height: 100vh;
  min-width: 0;
  background: #f5f7fb;
}

.admin-aside {
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  border-right: 1px solid #e4e7ed;
}

.admin-brand {
  flex-shrink: 0;
  height: 64px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 18px;
  border-bottom: 1px solid #ebeef5;
  box-sizing: border-box;
}

.admin-logo {
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.admin-title {
  color: #1f2d3d;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.admin-subtitle {
  color: #909399;
  font-size: 12px;
  margin-top: 2px;
}

.admin-menu {
  flex: 1;
  overflow-y: auto;
  border-right: none;
  padding: 8px 0;

  ::v-deep .el-submenu__title {
    height: 50px;
    line-height: 50px;
    font-weight: 500;
  }

  ::v-deep .el-submenu.is-active > .el-submenu__title,
  ::v-deep .el-submenu.is-active > .el-submenu__title i {
    color: #409eff;
  }

  ::v-deep .el-menu-item {
    height: 42px;
    line-height: 42px;
    font-size: 13px;
    min-width: 0;
  }

  ::v-deep .el-menu-item.is-active {
    background: #ecf5ff;
    box-shadow: inset 3px 0 #409eff;
    font-weight: 600;
  }
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-bottom: 1px solid #e4e7ed;
  padding: 0 20px;
  box-sizing: border-box;
}

.current-section {
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.lf-admin-layout > .el-container {
  min-width: 0;
}

.admin-main {
  padding: 0;
  overflow: auto;
}
</style>
